import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

/**
 * Phase 3.D — Temporary unlock window UI.
 *
 * Lives in the gate slide-panel beneath the manual control panel.
 * Shows the active window (if any) and the recent history. Manager
 * + Super Admin see action buttons (create, cancel); Viewer sees
 * the same data without the buttons.
 *
 * URL shape:
 *   - apiBase="customer" → /api/customer/gates/<id>/temp-unlocks
 *                          /api/customer/temp-unlocks/<id>/cancel
 *   - apiBase="admin"    → /api/admin/gates/<id>/temp-unlocks
 *                          /api/admin/temp-unlocks/<id>/cancel
 */
function GateTempUnlockSection({ gateId, apiBase = 'customer', canManage = true }) {
  const [data, setData] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const apiRoot = `/api/${apiBase}`;

  const load = () => {
    axios.get(`${apiRoot}/gates/${gateId}/temp-unlocks`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.error || 'שגיאה בטעינה'));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [gateId, apiBase]);

  // Refresh once a minute so a window that just expired stops looking
  // active without a manual refresh. Cheap; reuses the same endpoint.
  useEffect(() => {
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateId, apiBase]);

  const onCreate = async (payload) => {
    const res = await axios.post(`${apiRoot}/gates/${gateId}/temp-unlocks`, payload);
    setShowCreate(false);
    load();
    return res.data;
  };

  const onCancel = async (unlockId) => {
    if (!window.confirm('לבטל את השחרור הזמני? המערכת תחזור לפעול אוטומטית מיד.')) return;
    setBusy(true);
    try {
      await axios.post(`${apiRoot}/temp-unlocks/${unlockId}/cancel`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בביטול');
    } finally {
      setBusy(false);
    }
  };

  if (data === null && !error) return null;
  if (error) {
    return (
      <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginBottom: '0.5rem' }}>{error}</div>
    );
  }

  const active = data.active;
  const history = data.items || [];

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>
        שחרור נעילה זמני
      </h3>

      {active ? (
        <ActiveCard
          unlock={active}
          canManage={canManage}
          busy={busy}
          onCancel={() => onCancel(active.id)}
        />
      ) : (
        <NoActiveCard
          canManage={canManage}
          onCreate={() => setShowCreate(true)}
        />
      )}

      {history.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          <button
            type="button"
            onClick={() => setShowHistory(s => !s)}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#6b7280',
              fontSize: '0.78rem',
              padding: 0,
              cursor: 'pointer',
              fontFamily: 'Heebo, sans-serif',
            }}
          >
            {showHistory ? 'הסתר היסטוריה' : `הסטוריית שחרורים (${history.length})`}
          </button>
          {showHistory && (
            <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 0 0', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              {history.map(h => <HistoryRow key={h.id} unlock={h} />)}
            </ul>
          )}
        </div>
      )}

      {showCreate && createPortal(
        <CreateUnlockModal
          onClose={() => setShowCreate(false)}
          onSubmit={onCreate}
        />,
        document.body
      )}
    </div>
  );
}


function ActiveCard({ unlock, canManage, busy, onCancel }) {
  const endsAt = new Date(unlock.ends_at);
  const endsLabel = endsAt.toLocaleTimeString('he-IL', {
    timeZone: 'Asia/Jerusalem',
    hour: '2-digit', minute: '2-digit',
  });
  const minutesLeft = Math.max(0, Math.round((endsAt - Date.now()) / 60_000));
  return (
    <div style={{
      padding: '0.7rem 0.85rem',
      background: '#ecfdf5',
      border: '1px solid #a7f3d0',
      borderRadius: '6px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.25rem' }}>
        <span style={{ fontSize: '1rem' }}>🔓</span>
        <span style={{ fontWeight: 600, color: '#047857', fontSize: '0.9rem' }}>
          שחרור פעיל עד {endsLabel}
        </span>
        <span style={{ color: '#047857', fontSize: '0.75rem', marginInlineStart: 'auto' }}>
          (עוד {minutesLeft} דק׳)
        </span>
      </div>
      {unlock.created_by_name && (
        <div style={{ fontSize: '0.78rem', color: '#065f46' }}>
          נוצר ע"י: {unlock.created_by_name}
        </div>
      )}
      {unlock.notes && (
        <div style={{ fontSize: '0.78rem', color: '#065f46', marginTop: '0.2rem' }}>
          הערה: {unlock.notes}
        </div>
      )}
      {canManage && (
        <div style={{ marginTop: '0.6rem' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: '0.3rem 0.7rem',
              background: '#fef2f2',
              color: '#b91c1c',
              border: '1px solid #fecaca',
              borderRadius: '5px',
              fontSize: '0.78rem',
              cursor: busy ? 'wait' : 'pointer',
              fontFamily: 'Heebo, sans-serif',
            }}
          >
            בטל שחרור
          </button>
        </div>
      )}
    </div>
  );
}


function NoActiveCard({ canManage, onCreate }) {
  return (
    <div style={{
      padding: '0.7rem 0.85rem',
      background: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
      <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>אין שחרור פעיל</span>
      {canManage && (
        <button
          type="button"
          onClick={onCreate}
          style={{
            padding: '0.3rem 0.7rem',
            background: '#1a1a2e',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            fontSize: '0.78rem',
            cursor: 'pointer',
            fontFamily: 'Heebo, sans-serif',
          }}
        >
          + צור שחרור זמני
        </button>
      )}
    </div>
  );
}


function HistoryRow({ unlock }) {
  const start = new Date(unlock.starts_at);
  const end = new Date(unlock.ends_at);
  const dateLabel = start.toLocaleDateString('he-IL', {
    timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit',
  });
  const timeRange = `${start.toLocaleTimeString('he-IL', { timeZone: 'Asia/Jerusalem', hour: '2-digit', minute: '2-digit' })}–${end.toLocaleTimeString('he-IL', { timeZone: 'Asia/Jerusalem', hour: '2-digit', minute: '2-digit' })}`;
  const statusStyle = {
    active: { bg: '#ecfdf5', fg: '#047857', border: '#a7f3d0', label: 'פעיל' },
    scheduled: { bg: '#eff6ff', fg: '#1e40af', border: '#bfdbfe', label: 'מתוזמן' },
    ended: { bg: '#f3f4f6', fg: '#4b5563', border: '#e5e7eb', label: 'הסתיים' },
    cancelled: { bg: '#fef2f2', fg: '#b91c1c', border: '#fecaca', label: 'בוטל' },
  };
  const s = statusStyle[unlock.status] || statusStyle.ended;

  return (
    <li style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.45rem',
      alignItems: 'center',
      padding: '0.4rem 0.55rem',
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '5px',
      fontSize: '0.78rem',
    }}>
      <span style={{ color: '#9ca3af', minWidth: '70px' }}>{dateLabel}</span>
      <span style={{ minWidth: '95px' }}>{timeRange}</span>
      {unlock.created_by_name && (
        <span style={{ color: '#4a5568' }}>{unlock.created_by_name}</span>
      )}
      {unlock.notes && (
        <span style={{ color: '#6b7280', fontStyle: 'italic', flex: 1, minWidth: '120px' }}>{unlock.notes}</span>
      )}
      <span style={{
        marginInlineStart: 'auto',
        padding: '0.1rem 0.5rem',
        background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
        borderRadius: '999px',
        fontSize: '0.7rem', fontWeight: 500, whiteSpace: 'nowrap',
      }}>
        {s.label}
      </span>
    </li>
  );
}


function CreateUnlockModal({ onClose, onSubmit }) {
  // Default: starts now, ends 1 hour later. Display in Israel time.
  const nowIsoLocal = (() => {
    const d = new Date();
    d.setSeconds(0, 0);
    return formatLocal(d);
  })();
  const oneHourLaterIsoLocal = (() => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0, 0);
    return formatLocal(d);
  })();

  const [startsAt, setStartsAt] = useState(nowIsoLocal);
  const [endsAt, setEndsAt] = useState(oneHourLaterIsoLocal);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const startDate = new Date(startsAt);
    const endDate = new Date(endsAt);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      setError('זמני התחלה וסיום חייבים להיות תקינים');
      return;
    }
    if (endDate <= startDate) {
      setError('שעת הסיום חייבת להיות אחרי שעת ההתחלה');
      return;
    }
    if ((endDate - startDate) > 24 * 60 * 60 * 1000) {
      setError('חלון השחרור לא יכול לחרוג מ-24 שעות');
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        starts_at: startDate.toISOString(),
        ends_at: endDate.toISOString(),
        notes: notes.trim() || null,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה ביצירה');
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <form
        className="modal"
        onSubmit={handleSubmit}
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 101,
          width: 'min(440px, calc(100vw - 24px))',
        }}
      >
        <h2 style={{ marginBottom: '0.5rem', fontSize: '1.05rem', fontWeight: 600 }}>
          שחרור נעילה זמני
        </h2>
        <p style={{ color: '#6b7280', fontSize: '0.8rem', marginBottom: '1rem' }}>
          במהלך החלון, הסגירה האוטומטית (סיום יום הפעילות + נעילה רצופה) לא תפעל על השער.
        </p>

        <div className="form-group">
          <label>שעת התחלה</label>
          <input
            type="datetime-local"
            value={startsAt}
            onChange={e => setStartsAt(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>שעת סיום</label>
          <input
            type="datetime-local"
            value={endsAt}
            onChange={e => setEndsAt(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>הערה (אופציונלי)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="לדוגמה: צוות מטעין משאית"
            rows={2}
            style={{
              width: '100%',
              padding: '0.55rem 0.75rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontFamily: 'Heebo, sans-serif',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {error && (
          <div style={{
            background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca',
            padding: '0.45rem 0.7rem', borderRadius: '5px',
            fontSize: '0.8rem', marginTop: '0.6rem',
          }}>
            {error}
          </div>
        )}

        <div className="modal-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'יוצר...' : 'צור שחרור'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            ביטול
          </button>
        </div>
      </form>
    </>
  );
}


// ``<input type="datetime-local">`` wants ``YYYY-MM-DDTHH:MM`` (no
// seconds, no timezone). The browser interprets that string in the
// user's *local* time zone, which is what the manager intuits. We
// then send it on as a Date → ISO with offset, so the backend always
// receives an unambiguous UTC instant.
function formatLocal(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}


export default GateTempUnlockSection;
